/**
 * Altar Forge odds, costs, pity and expected-value hints (crafting.md §3–§4, §7.3). Everything
 * here is PURE and draws nothing: it powers the live odds panel, the pity meters and the "one
 * more run buys…" hints, and forge.ts uses the same functions to roll, so what the panel shows is
 * exactly what is rolled.
 */
import type { ClassId } from '@keystone/sim';
import { codexLuck } from './codex';
import {
  FREE_TEMPER_MIN_CAP, // +econ-pacing
  BLESSED_LUCK,
  BLESSED_TEMPER_PP,
  CRAFT_FANGSTEEL,
  CRAFT_GOLD,
  CRAFT_SCALES,
  DRILL_BASE,
  DRILL_GOLD,
  DRILL_VENOM,
  EPIC_NEAR_MISS_WINDOW,
  EPIC_PITY_HARD,
  EPIC_SOFT_FROM,
  EPIC_SOFT_STEP,
  EYE_CHANCES,
  EYE_FRAGMENTS_PER_EYE,
  FAILSTACK_PP,
  FAVORED_LUCK,
  FORTUNE_EVERY,
  FORTUNE_LUCK,
  GILD_BASE,
  GILD_GOLD,
  GILD_MAX,
  GILD_SCALES,
  GRADE_CRAFT_ILVL,
  GRADE_GOLD,
  LUCK_CAP,
  MOLTSKIN_MAX_CRAFT,
  MW_BASE,
  MW_COST,
  NEAR_MISS_WINDOW,
  RARE_FLOOR_AFTER,
  RARITY_FORMULA,
  REFUND_FRACTION,
  RESONANCE_MAX,
  RESONANCE_NEAR_MISS,
  RESONANCE_PER_FAIL,
  SLOT_MULT,
  SURGE_CHANCE,
  AWAKEN_EVERY,
  AWAKEN_TABLE,
  TEMPER_COST,
  TEMPER_VENOM_MAX,
  TEMPER_VENOM_PP,
  VAULT_TOKEN_LUCK,
  VENOM_MAX_CRAFT,
  gradeForIlvl,
  REFINE_GOLD,
  CATALYST_FANGSTEEL,
  DRILL_MAX_SOCKETS,
  temperBase,
  EYE_ATTUNE_FRAGMENTS, // +forge-sinks
  EYE_ATTUNE_GOLD, // +forge-sinks
  SIGIL_RESHAPE_GOLD, // +forge-sinks
} from './forge.constants';
import { favoredSlotFor, isLocalDate } from './forge.dates';
import { itemBandGrade, itemForge, masterworkCeiling, rarityIndex } from './forge.items';
import { planScales, shortfall } from './forge.wallet';
import { MASTERWORK_MAX } from './items';
import { legendariesFor } from './items.legendaries';
import { hasUnlock, tierDef, tierFor, totalRating } from './rating';
import { estimateRunResources } from './payout';
import { isTierSlot } from './sets';
import type { ForgeState, Item, ItemForgeState, ItemSlot, Profile, Rarity, ResourceBundle, ScaleGrade, SecondaryStat } from './types';
import { RARITIES } from './types';

// ============================================================================
// Rarity odds
// ============================================================================

/** Percent per rarity (sums to 100). */
export type RarityOdds = Record<Rarity, number>;

const CRAFT_RARITIES: readonly Rarity[] = ['common', 'uncommon', 'rare', 'epic'];

const zeroOdds = (): RarityOdds => ({ common: 0, uncommon: 0, rare: 0, epic: 0, legendary: 0 });

/** Rarity % before floors and pity for Luck Λ (crafting.md §3.2; Λ is clamped to 0..LUCK_CAP). */
export function baseRarityOdds(luck: number): RarityOdds {
  const l = Math.max(0, Math.min(LUCK_CAP, luck));
  const epic = RARITY_FORMULA.epic(l);
  const common = RARITY_FORMULA.common(l);
  const rare = Math.min(RARITY_FORMULA.rareMax, RARITY_FORMULA.rare(l), 100 - epic - common);
  return { common, uncommon: 100 - common - rare - epic, rare, epic, legendary: 0 };
}

/** Move every % below `floor` up to `floor` ("Rare 20 + Common 40 + Uncommon 35 → Rare 95"). */
export function applyRarityFloor(odds: RarityOdds, floor: Rarity | null): RarityOdds {
  if (!floor) return { ...odds };
  const out = { ...odds };
  const fi = rarityIndex(floor);
  for (const r of CRAFT_RARITIES) {
    if (rarityIndex(r) < fi) {
      out[floor] += out[r];
      out[r] = 0;
    }
  }
  return out;
}

/**
 * Epic pity for the `craftNo`-th craft since the last Epic (1-based): from EPIC_SOFT_FROM on,
 * Epic gains EPIC_SOFT_STEP pp per craft past EPIC_SOFT_FROM − 1, taken from Common, then
 * Uncommon, then Rare; at EPIC_PITY_HARD it is 100%.
 */
export function applyEpicPity(odds: RarityOdds, craftNo: number): RarityOdds {
  const out = { ...odds };
  if (craftNo >= EPIC_PITY_HARD) return { ...zeroOdds(), epic: 100 };
  if (craftNo < EPIC_SOFT_FROM) return out;
  let add = EPIC_SOFT_STEP * (craftNo - (EPIC_SOFT_FROM - 1));
  for (const r of ['common', 'uncommon', 'rare'] as const) {
    const take = Math.min(out[r], add);
    out[r] -= take;
    out.epic += take;
    add -= take;
  }
  return out;
}

/** Bands of the one rarity roll r ∈ [0,100), bottom-up Common → Uncommon → Rare → Epic. */
export type RarityBands = Record<'common' | 'uncommon' | 'rare' | 'epic', [number, number]>;

export function rarityBands(odds: RarityOdds): RarityBands {
  let lo = 0;
  const out = {} as RarityBands;
  for (const r of CRAFT_RARITIES as readonly ('common' | 'uncommon' | 'rare' | 'epic')[]) {
    const hi = r === 'epic' ? 100 : lo + odds[r];
    out[r] = [lo, hi];
    lo = hi;
  }
  return out;
}

/** The rarity a roll r ∈ [0,100) lands in. */
export function rarityForRoll(bands: RarityBands, r: number): Rarity {
  for (const x of ['common', 'uncommon', 'rare'] as const) if (r < bands[x][1] && bands[x][1] > bands[x][0]) return x;
  return 'epic';
}

/** A non-Epic roll within EPIC_NEAR_MISS_WINDOW below the Epic band (real progress: +2 pity). */
export function isEpicNearMiss(bands: RarityBands, r: number): boolean {
  const start = bands.epic[0];
  return start < 100 && r < start && start - r <= EPIC_NEAR_MISS_WINDOW;
}

// ============================================================================
// Craft spec, luck, costs
// ============================================================================

export type CraftPayer = 'wallet' | 'satchel';

export interface CraftSpec {
  /** Who the item is for (any character; drives stat priority, set and legendary). */
  classId: ClassId;
  slot: ItemSlot;
  /** Scale grade (sets the crafted ilvl). Ignored when `token` is used (the token's grade). */
  grade: ScaleGrade;
  /** Viper Venom, 0..VENOM_MAX_CRAFT (+1 Luck each). */
  venom?: number;
  /** Moltskin 0..2: 1 = choose `stats[0]`, 2 = both stats (on a trinket: choose `trinketEffectId`). */
  moltskin?: number;
  stats?: SecondaryStat[];
  trinketEffectId?: string;
  /** Commit an Altar Sigil: guaranteed tier piece (tier slots only), Rare floor. */
  sigil?: boolean;
  /** Commit a Serpent's Eye (EYE_FRAGMENTS_PER_EYE fragments): legendary roll, Epic floor. Champion+. */
  eye?: boolean;
  /** Spend a Blessed Charge (+BLESSED_LUCK). */
  blessed?: boolean;
  /** Use wallet.vaultTokens[token]: free craft at its grade, Rare floor, +VAULT_TOKEN_LUCK. */
  token?: number;
  /** Caller's local date 'YYYY-MM-DD' (Favored Slot of the Day). Omitted = no favored bonus. */
  localDate?: string;
  /** 'satchel' = a companion's own purse: no gold, base odds, its own pity (crafting.md §11). */
  payer?: CraftPayer;
  /** Equip the result on `classId` (default false: it goes to the bag). */
  equip?: boolean;
}

export interface LuckBreakdown {
  venom: number;
  blessed: number;
  favored: number;
  fortune: number;
  codex: number;
  token: number;
  /** Sum before the cap. */
  raw: number;
  /** min(raw, LUCK_CAP): what the odds use. */
  total: number;
  cap: number;
}

/** Fangsteel / Scales / gold of a plain craft in `slot` at `grade` (before trade-down). */
export function craftCost(slot: ItemSlot, grade: ScaleGrade): { fangsteel: number; scales: number; gold: number } {
  return {
    fangsteel: Math.round(CRAFT_FANGSTEEL * SLOT_MULT[slot]),
    scales: CRAFT_SCALES,
    gold: Math.round(CRAFT_GOLD * SLOT_MULT[slot] * GRADE_GOLD[grade]),
  };
}

/** Is the next player craft a Fortune craft (+FORTUNE_LUCK)? */
export const isFortuneCraft = (craftCount: number): boolean => craftCount % FORTUNE_EVERY === FORTUNE_EVERY - 1;

/** Crafts until the Fortune craft, counting it (1 = the next craft is Fortune). */
export const fortuneIn = (craftCount: number): number => ((FORTUNE_EVERY - 1 - (craftCount % FORTUNE_EVERY) + FORTUNE_EVERY) % FORTUNE_EVERY) + 1;

export function craftLuck(forge: Pick<ForgeState, 'craftCount' | 'codex'>, spec: CraftSpec): LuckBreakdown {
  const bot = spec.payer === 'satchel';
  const venom = Math.max(0, Math.min(VENOM_MAX_CRAFT, Math.floor(spec.venom ?? 0)));
  const blessed = !bot && spec.blessed ? BLESSED_LUCK : 0;
  const favored = !bot && spec.localDate && isLocalDate(spec.localDate) && favoredSlotFor(spec.localDate) === spec.slot ? FAVORED_LUCK : 0;
  const fortune = !bot && isFortuneCraft(forge.craftCount) ? FORTUNE_LUCK : 0;
  const codex = bot ? 0 : codexLuck(forge.codex);
  const token = !bot && spec.token !== undefined ? VAULT_TOKEN_LUCK : 0;
  const raw = venom + blessed + favored + fortune + codex + token;
  return { venom, blessed, favored, fortune, codex, token, raw, total: Math.min(LUCK_CAP, raw), cap: LUCK_CAP };
}

/** Pity counters a craft reads (the player's forge, or a companion's satchel). */
export interface CraftPity {
  sinceEpic: number;
  belowRare: number;
  eyeMisses: number;
}

export function craftPity(p: Profile, spec: Pick<CraftSpec, 'payer' | 'classId'>): CraftPity {
  if (spec.payer === 'satchel') {
    const s = p.characters[spec.classId]?.satchel;
    return { sinceEpic: s?.sinceEpic ?? 0, belowRare: s?.belowRare ?? 0, eyeMisses: 0 };
  }
  return { sinceEpic: p.forge.sinceEpic, belowRare: p.forge.belowRare, eyeMisses: p.forge.eyeMisses };
}

/** Final rarity odds for a craft (floors, then Epic pity). Legendary is a separate Eye roll. */
export function craftRarityOdds(luck: number, floor: Rarity | null, pity: Pick<CraftPity, 'sinceEpic'>): RarityOdds {
  return applyEpicPity(applyRarityFloor(baseRarityOdds(luck), floor), pity.sinceEpic + 1);
}

/** Legendary chance (%) of the next Eye after `eyeMisses` consecutive misses. */
export const eyeChance = (eyeMisses: number): number => EYE_CHANCES[Math.min(EYE_CHANCES.length - 1, Math.max(0, eyeMisses))]!;

export type CraftBlock =
  | 'unknownClass'
  | 'cantAfford'
  | 'notEnoughGold'
  | 'notTierSlot'
  | 'noLegendary'
  | 'locked'
  | 'reagentConflict'
  | 'noToken'
  | 'badReagent';

export interface CraftOdds {
  /** Final % per rarity (sums to 100; `legendary` is the Eye hit chance folded in). */
  rarity: RarityOdds;
  /** Rarity % before the Eye roll (what the rarity roll uses). */
  rollOdds: RarityOdds;
  bands: RarityBands;
  /** Legendary % (Eye crafts only), else null. */
  legendary: number | null;
  /** Every floor in play and the one applied (highest). */
  floors: { sigil: boolean; eye: boolean; token: boolean; rarePity: boolean };
  floor: Rarity | null;
  luck: LuckBreakdown;
  pity: PityState;
  fortune: boolean;
  /** What will be paid (Scales already split by trade-down). */
  cost: ResourceBundle;
  shortfall: ResourceBundle;
  grade: ScaleGrade;
  ilvl: number;
  uncappedIlvl: number;
  /** "Your tier caps this at N" text, or null. */
  capNote: string | null;
  tierPiece: boolean;
  /** Why the craft can't run (null = it can). */
  blocked: CraftBlock | null;
}

/**
 * Live odds for a craft spec: pure, draws nothing. `blocked` says why `craft` would refuse it.
 */
export function craftOdds(p: Profile, spec: CraftSpec): CraftOdds {
  const bot = spec.payer === 'satchel';
  let blocked: CraftBlock | null = null;
  const block = (b: CraftBlock): void => {
    blocked ??= b;
  };
  if (!p.characters[spec.classId]) block('unknownClass');
  const token = spec.token !== undefined ? p.wallet.vaultTokens[spec.token] : undefined;
  if (spec.token !== undefined && (!token || bot)) block('noToken');
  const grade = token ? token.grade : spec.grade;
  const tier = tierDef(standingTier(p));
  const uncappedIlvl = GRADE_CRAFT_ILVL[grade];
  const ilvl = Math.min(tier.maxIlvl, uncappedIlvl);
  if (bot && (spec.sigil || spec.eye || spec.blessed)) block('badReagent');
  if (spec.sigil && spec.eye) block('reagentConflict');
  if (spec.sigil && !isTierSlot(spec.slot)) block('notTierSlot');
  if (spec.eye) {
    if (!hasUnlock(totalRatingOf(p), 'legendaries')) block('locked');
    else if (!legendariesFor(spec.classId).some((l) => l.slot === spec.slot)) block('noLegendary');
  }
  const molt = Math.floor(spec.moltskin ?? 0);
  if (molt < 0 || molt > MOLTSKIN_MAX_CRAFT || (spec.venom ?? 0) < 0 || (spec.venom ?? 0) > VENOM_MAX_CRAFT) block('badReagent');
  if (molt >= 1 && spec.slot !== 'trinket' && (spec.stats?.length ?? 0) < molt) block('badReagent');
  if (molt >= 1 && spec.slot === 'trinket' && !spec.stats?.length) block('badReagent');
  if (molt === 2 && spec.slot === 'trinket' && !spec.trinketEffectId) block('badReagent');
  if (molt >= 1 && spec.stats && new Set(spec.stats.slice(0, 2)).size !== Math.min(2, spec.stats.length)) block('badReagent');

  const luck = craftLuck(p.forge, spec);
  const pity = craftPity(p, spec);
  const rarePity = pity.belowRare >= RARE_FLOOR_AFTER;
  const floors = { sigil: !!spec.sigil, eye: !!spec.eye, token: !!token, rarePity };
  const floor: Rarity | null = spec.eye ? 'epic' : spec.sigil || token || rarePity ? 'rare' : null;
  const rollOdds = craftRarityOdds(luck.total, floor, pity);
  const legendary = spec.eye ? eyeChance(pity.eyeMisses) : null;
  const rarity = { ...rollOdds };
  if (legendary !== null) {
    rarity.legendary = legendary;
    rarity.epic = 100 - legendary;
  }

  const cost = craftBundle(p, spec, grade);
  const short = cost ? (bot ? satchelShortfall(p, spec.classId, cost) : shortfall(p, cost)) : { scales: { [grade]: CRAFT_SCALES } };
  if (!cost || Object.keys(short).length) {
    const onlyGold = cost && Object.keys(short).length === 1 && short.gold;
    block(onlyGold ? 'notEnoughGold' : 'cantAfford');
  }
  return {
    rarity,
    rollOdds,
    bands: rarityBands(rollOdds),
    legendary,
    floors,
    floor,
    luck,
    pity: pityState(p, spec),
    fortune: luck.fortune > 0,
    cost: cost ?? {},
    shortfall: short,
    grade,
    ilvl,
    uncappedIlvl,
    capNote: ilvl < uncappedIlvl ? `Your ${tier.name} rank caps this at item level ${ilvl}` : null,
    tierPiece: !!spec.sigil && isTierSlot(spec.slot),
    blocked,
  };
}

/**
 * The resources a craft takes (null when the Scales can't be covered even with trade-down).
 * A satchel craft is paid from the satchel (checked by forge.ts); its gold is 0.
 */
export function craftBundle(p: Profile, spec: CraftSpec, grade: ScaleGrade = spec.grade): ResourceBundle | null {
  const bot = spec.payer === 'satchel';
  const out: ResourceBundle = {};
  const venom = Math.max(0, Math.floor(spec.venom ?? 0));
  const molt = Math.max(0, Math.floor(spec.moltskin ?? 0));
  if (venom) out.venom = venom;
  if (molt) out.moltskin = molt;
  if (spec.sigil) out.sigils = 1;
  if (spec.eye) out.eyeFragments = EYE_FRAGMENTS_PER_EYE;
  if (spec.blessed) out.blessed = 1;
  if (spec.token !== undefined && !bot) return out;
  const c = craftCost(spec.slot, grade);
  out.fangsteel = c.fangsteel;
  // The very first player craft (the First Offering tutorial) waives its gold: a new profile has none.
  if (!bot && p.forge.craftCount > 0) out.gold = c.gold;
  const have = bot ? (p.characters[spec.classId]?.satchel?.scales ?? { ashen: 0, jade: 0, gilded: 0, eternal: 0 }) : p.wallet.scales;
  const plan = planScales(have, grade, c.scales);
  if (!plan) return null;
  out.scales = plan;
  return out;
}

// ============================================================================
// Pity meters
// ============================================================================

export interface PityState {
  epic: { count: number; hard: number; softFrom: number; guaranteedIn: number; soft: boolean; nextEpicBonus: number };
  rareFloor: { count: number; after: number; next: boolean };
  eye: { misses: number; nextChance: number; guaranteedBy: number };
  fortune: { craftsUntil: number; next: boolean };
}

/** The pity meters (player forge by default; a satchel's with payer 'satchel'). */
export function pityState(p: Profile, spec: Pick<CraftSpec, 'payer' | 'classId'> = { classId: p.mainClassId }): PityState {
  const pity = craftPity(p, spec);
  const craftNo = pity.sinceEpic + 1;
  const bot = spec.payer === 'satchel';
  const guaranteedIn = Math.max(1, EPIC_PITY_HARD - pity.sinceEpic);
  return {
    epic: {
      count: pity.sinceEpic,
      hard: EPIC_PITY_HARD,
      softFrom: EPIC_SOFT_FROM,
      guaranteedIn,
      soft: craftNo >= EPIC_SOFT_FROM,
      nextEpicBonus: craftNo >= EPIC_PITY_HARD ? 100 : craftNo >= EPIC_SOFT_FROM ? EPIC_SOFT_STEP * (craftNo - (EPIC_SOFT_FROM - 1)) : 0,
    },
    rareFloor: { count: pity.belowRare, after: RARE_FLOOR_AFTER, next: pity.belowRare >= RARE_FLOOR_AFTER },
    eye: { misses: pity.eyeMisses, nextChance: eyeChance(pity.eyeMisses), guaranteedBy: Math.max(1, EYE_CHANCES.length - pity.eyeMisses) },
    fortune: bot ? { craftsUntil: 0, next: false } : { craftsUntil: fortuneIn(p.forge.craftCount), next: isFortuneCraft(p.forge.craftCount) },
  };
}

// ============================================================================
// Tempering, masterwork, drill, gild (percentage rolls with failstack + Resonance)
// ============================================================================


export interface RollBoosts {
  /** Venom vials, 0..TEMPER_VENOM_MAX (+TEMPER_VENOM_PP each). */
  venom?: number;
  /** Spend a Blessed Charge (+BLESSED_TEMPER_PP). */
  blessed?: boolean;
}

export interface ChanceBreakdown {
  base: number;
  failstack: number;
  venom: number;
  blessed: number;
  /** Resonance is full: this attempt is guaranteed. */
  guaranteed: boolean;
  /** Final % (1..100). */
  chance: number;
}

/** Success % of a pity-backed roll: min(100, base + 10·fails + 5·venom + 15·blessed), 100 at full Resonance. */
export function rollChance(base: number, f: Pick<ItemForgeState, 'fails' | 'resonance'>, boosts: RollBoosts = {}): ChanceBreakdown {
  const venomN = Math.max(0, Math.min(TEMPER_VENOM_MAX, Math.floor(boosts.venom ?? 0)));
  const failstack = FAILSTACK_PP * f.fails;
  const venom = TEMPER_VENOM_PP * venomN;
  const blessed = boosts.blessed ? BLESSED_TEMPER_PP : 0;
  const guaranteed = f.resonance >= RESONANCE_MAX;
  const chance = guaranteed ? 100 : Math.max(1, Math.min(100, base + failstack + venom + blessed));
  return { base, failstack, venom, blessed, guaranteed, chance };
}

/** Worst-case attempts until success, counting this one (Resonance makes it finite). */
export function guaranteedWithin(f: Pick<ItemForgeState, 'resonance'>, chance: number): number {
  if (chance >= 100 || f.resonance >= RESONANCE_MAX) return 1;
  return Math.ceil((RESONANCE_MAX - f.resonance) / RESONANCE_PER_FAIL) + 1;
}

/** Near miss on a failed roll r (1..100) that needed ≤ chance. */
export const isTemperNearMiss = (r: number, chance: number): boolean => r > chance && r - chance <= NEAR_MISS_WINDOW;

/**
 * Exact expected attempts to succeed (same boosts every attempt), and the probability of
 * succeeding on attempt 1, 2, … . Near misses (+35 Resonance) are modelled.
 */
export function expectedAttempts(base: number, f: Pick<ItemForgeState, 'fails' | 'resonance'>, boosts: RollBoosts = {}): { expected: number; byAttempt: number[] } {
  const byAttempt: number[] = [];
  // Distribution over states per attempt (fails, resonance) → probability mass.
  let states = new Map<string, { fails: number; resonance: number; p: number }>([[`${f.fails}:${f.resonance}`, { fails: f.fails, resonance: f.resonance, p: 1 }]]);
  let expected = 0;
  for (let n = 1; states.size && n <= 50; n++) {
    let succ = 0;
    const next = new Map<string, { fails: number; resonance: number; p: number }>();
    for (const s of states.values()) {
      const c = rollChance(base, s, boosts).chance;
      succ += s.p * (c / 100);
      const near = Math.max(0, Math.min(NEAR_MISS_WINDOW, 100 - c)) / 100;
      const plain = Math.max(0, 1 - c / 100 - near);
      const add = (res: number, q: number): void => {
        if (q <= 0) return;
        const st = { fails: s.fails + 1, resonance: Math.min(RESONANCE_MAX, s.resonance + res) };
        const k = `${st.fails}:${st.resonance}`;
        const cur = next.get(k);
        if (cur) cur.p += s.p * q;
        else next.set(k, { ...st, p: s.p * q });
      };
      add(RESONANCE_NEAR_MISS, near);
      add(RESONANCE_PER_FAIL, plain);
    }
    byAttempt.push(succ);
    expected += n * succ;
    states = next;
  }
  return { expected, byAttempt };
}

export type TemperBlock = 'unknownItem' | 'capped' | 'cantAfford' | 'notEnoughGold' | 'badReagent';

export interface TemperOdds {
  from: number;
  target: number;
  /** Target on a Surge (two steps), or null when there is no headroom for it. */
  surgeTarget: number | null;
  surgeChance: number;
  odds: ChanceBreakdown;
  resonance: number;
  fails: number;
  guaranteedWithin: number;
  expected: { attempts: number; byAttempt: number[] };
  /** This step (if it succeeds) triggers an Awakening roll. */
  awakening: boolean;
  awakenTable: typeof AWAKEN_TABLE;
  grade: ScaleGrade;
  cost: ResourceBundle;
  /** What a failure gives back. */
  refundOnFail: ResourceBundle;
  shortfall: ResourceBundle;
  cap: number;
  blocked: TemperBlock | null;
  /** +forge-sinks: this attempt uses a free temper (Fangsteel, Scales and gold waived; reagents still paid). */
  free: boolean;
}

/** Wallet or satchel scales of the payer. */
function payerScales(p: Profile, payer: CraftPayer, classId?: ClassId): Record<ScaleGrade, number> {
  if (payer === 'satchel') return p.characters[classId ?? '']?.satchel?.scales ?? { ashen: 0, jade: 0, gilded: 0, eternal: 0 };
  return p.wallet.scales;
}

export function refundOf(cost: ResourceBundle): ResourceBundle {
  const out: ResourceBundle = {};
  const fs = Math.floor((cost.fangsteel ?? 0) * REFUND_FRACTION);
  if (fs) out.fangsteel = fs;
  // Scales: REFUND_FRACTION of the count, rounded up, given back highest grade first.
  let left = Math.ceil(scaleTotal(cost) * REFUND_FRACTION);
  const grades = (['eternal', 'gilded', 'jade', 'ashen'] as const).filter((g) => (cost.scales?.[g] ?? 0) > 0);
  for (const g of grades) {
    if (left <= 0) break;
    const n = Math.min(left, cost.scales![g]!);
    out.scales = { ...out.scales, [g]: n };
    left -= n;
  }
  return out;
}

const scaleTotal = (b: ResourceBundle): number => (b.scales ? Object.values(b.scales).reduce((s, n) => s + (n ?? 0), 0) : 0);

export interface TemperOpts extends RollBoosts {
  payer?: CraftPayer;
  /** Satchel payer: whose satchel (default: the item's wearer). */
  classId?: ClassId;
  /**
   * +forge-sinks: spend a free attempt (forge.freeTempers) if one is left. Opt-in so autopilot,
   * bots and the harness never consume the new player's free first temper; the web screens pass it.
   */
  useFree?: boolean;
}

/** Live odds and costs for one temper attempt on an item. Pure. */
export function temperOdds(p: Profile, itemOrId: Item | string, opts: TemperOpts = {}): TemperOdds {
  const item = typeof itemOrId === 'string' ? p.inventory.find((i) => i.id === itemOrId) : itemOrId;
  const payer = opts.payer ?? 'wallet';
  const free = !!opts.useFree && hasFreeTemper(p, payer); // +forge-sinks
  // +econ-pacing: the free temper reaches FREE_TEMPER_MIN_CAP even at the Novice cap (day one).
  const cap = free ? Math.max(FREE_TEMPER_MIN_CAP, tierDef(standingTier(p)).maxIlvl) : tierDef(standingTier(p)).maxIlvl;
  let blocked: TemperBlock | null = item ? null : 'unknownItem';
  const from = item?.ilvl ?? 0;
  const target = Math.min(cap, from + 5);
  if (item && from >= cap) blocked = 'capped';
  const f = itemForge(item ?? {});
  const grade = gradeForIlvl(Math.max(target, 201));
  const c = TEMPER_COST[grade];
  if (payer === 'satchel' && (opts.venom || opts.blessed)) {
    const s = p.characters[opts.classId ?? ''];
    if (opts.blessed || (opts.venom ?? 0) > (s?.satchel?.venom ?? 0)) blocked ??= 'badReagent';
  }
  if ((opts.venom ?? 0) < 0 || (opts.venom ?? 0) > TEMPER_VENOM_MAX) blocked ??= 'badReagent';
  const plan = free ? {} : planScales(payerScales(p, payer, opts.classId), grade, c.scales);
  const cost: ResourceBundle = free ? {} : { fangsteel: c.fangsteel, scales: plan ?? { [grade]: c.scales } };
  if (payer === 'wallet' && !free) cost.gold = c.gold;
  if (opts.venom) cost.venom = Math.floor(opts.venom);
  if (opts.blessed) cost.blessed = 1;
  const short = payer === 'wallet' ? shortfall(p, cost) : satchelShortfall(p, opts.classId, cost);
  if (!blocked && (!plan || Object.keys(short).length)) blocked = Object.keys(short).length === 1 && short.gold ? 'notEnoughGold' : 'cantAfford';
  const odds = rollChance(temperBase(target), f, opts);
  const ex = expectedAttempts(temperBase(target), f, opts);
  const nextSteps = f.steps + 1;
  return {
    from,
    target,
    surgeTarget: target + 5 <= cap ? target + 5 : null,
    surgeChance: SURGE_CHANCE,
    odds,
    resonance: f.resonance,
    fails: f.fails,
    guaranteedWithin: guaranteedWithin(f, odds.chance),
    expected: { attempts: ex.expected, byAttempt: ex.byAttempt },
    awakening: Math.floor(nextSteps / AWAKEN_EVERY) > Math.floor(f.steps / AWAKEN_EVERY),
    awakenTable: AWAKEN_TABLE,
    grade,
    cost,
    refundOnFail: refundOf(cost),
    shortfall: short,
    cap,
    blocked,
    free,
  };
}

/** +forge-sinks: the payer has a free temper / masterwork attempt waiting (wallet only, never satchels). */
export const hasFreeTemper = (p: Pick<Profile, 'forge'>, payer: CraftPayer = 'wallet'): boolean => payer === 'wallet' && (p.forge.freeTempers ?? 0) > 0;

/** What a satchel is missing to pay `cost` (gold is never charged to satchels). */
export function satchelShortfall(p: Profile, classId: ClassId | undefined, cost: ResourceBundle): ResourceBundle {
  const s = p.characters[classId ?? '']?.satchel;
  const out: ResourceBundle = {};
  if (!s) return { fangsteel: cost.fangsteel ?? 0 };
  if ((cost.fangsteel ?? 0) > s.fangsteel) out.fangsteel = (cost.fangsteel ?? 0) - s.fangsteel;
  if ((cost.venom ?? 0) > s.venom) out.venom = (cost.venom ?? 0) - s.venom;
  if ((cost.moltskin ?? 0) > s.moltskin) out.moltskin = (cost.moltskin ?? 0) - s.moltskin;
  for (const [g, n] of Object.entries(cost.scales ?? {})) {
    const have = s.scales[g as ScaleGrade];
    if ((n ?? 0) > have) out.scales = { ...out.scales, [g]: (n ?? 0) - have };
  }
  return out;
}

export type MasterworkBlock = 'unknownItem' | 'notAtCap' | 'maxMasterwork' | 'cantAfford' | 'notEnoughGold' | 'badReagent';

export interface MasterworkOdds {
  step: number;
  ceiling: number;
  odds: ChanceBreakdown;
  guaranteedWithin: number;
  cost: ResourceBundle;
  refundOnFail: ResourceBundle;
  shortfall: ResourceBundle;
  blocked: MasterworkBlock | null;
  /** +forge-sinks: free attempt (see TemperOdds.free). */
  free: boolean;
}

/** Masterwork +1 odds (at the tier cap, up to the rarity ceiling). Pure. */
export function masterworkOdds(p: Profile, itemOrId: Item | string, opts: TemperOpts = {}): MasterworkOdds {
  const item = typeof itemOrId === 'string' ? p.inventory.find((i) => i.id === itemOrId) : itemOrId;
  const cap = tierDef(standingTier(p)).maxIlvl;
  const payer = opts.payer ?? 'wallet';
  const mw = item?.masterwork ?? 0;
  const ceiling = item ? masterworkCeiling(item) : 0;
  let blocked: MasterworkBlock | null = !item ? 'unknownItem' : item.ilvl < cap ? 'notAtCap' : mw >= ceiling || mw >= MASTERWORK_MAX ? 'maxMasterwork' : null;
  const step = Math.min(mw, MW_COST.length - 1);
  const c = MW_COST[step]!;
  const grade = item ? itemBandGrade(item) : 'ashen';
  const free = !!opts.useFree && hasFreeTemper(p, payer); // +forge-sinks
  const plan = free ? {} : planScales(payerScales(p, payer, opts.classId), grade, c.scales);
  const cost: ResourceBundle = free ? {} : { fangsteel: c.fangsteel, scales: plan ?? { [grade]: c.scales } };
  if (payer === 'wallet' && !free) cost.gold = c.gold;
  if (opts.venom) cost.venom = Math.floor(opts.venom);
  if (opts.blessed) cost.blessed = 1;
  if ((opts.venom ?? 0) < 0 || (opts.venom ?? 0) > TEMPER_VENOM_MAX || (payer === 'satchel' && opts.blessed)) blocked ??= 'badReagent';
  const short = payer === 'wallet' ? shortfall(p, cost) : satchelShortfall(p, opts.classId, cost);
  if (!blocked && (!plan || Object.keys(short).length)) blocked = Object.keys(short).length === 1 && short.gold ? 'notEnoughGold' : 'cantAfford';
  const f = itemForge(item ?? {});
  const odds = rollChance(MW_BASE[step]!, f, opts);
  return { step: mw + 1, ceiling, odds, guaranteedWithin: guaranteedWithin(f, odds.chance), cost, refundOnFail: refundOf(cost), shortfall: short, blocked, free };
}

export interface SimpleRollOdds {
  odds: ChanceBreakdown;
  cost: ResourceBundle;
  refundOnFail: ResourceBundle;
  shortfall: ResourceBundle;
  guaranteedWithin: number;
  blocked: string | null;
}

/** Drill a socket: DRILL_VENOM Venom + DRILL_GOLD × grade gold, 50% base with the pity rules. */
export function drillOdds(p: Profile, item: Item, boosts: RollBoosts = {}): SimpleRollOdds {
  const max = drillMaxSockets(item);
  const cost: ResourceBundle = { venom: DRILL_VENOM + Math.floor(boosts.venom ?? 0), gold: Math.round(DRILL_GOLD * GRADE_GOLD[itemBandGrade(item)]) };
  if (boosts.blessed) cost.blessed = 1;
  const short = shortfall(p, cost);
  const f = itemForge(item);
  const odds = rollChance(DRILL_BASE, f, boosts);
  // A failed drill refunds half its Venom (the item never changes).
  const refund: ResourceBundle = { venom: Math.floor(DRILL_VENOM * REFUND_FRACTION) };
  const blocked = item.sockets >= max ? 'maxSockets' : Object.keys(short).length ? (Object.keys(short).length === 1 && short.gold ? 'notEnoughGold' : 'cantAfford') : null;
  return { odds, cost, refundOnFail: refund, shortfall: short, guaranteedWithin: guaranteedWithin(f, odds.chance), blocked };
}

/** Most sockets a drill can reach on this item (rarity cap; never below what it has). */
export const drillMaxSockets = (item: Pick<Item, 'rarity'>): number => DRILL_MAX_SOCKETS[item.rarity];

/** Gilding (cosmetic): GILD_SCALES Eternal Scales + GILD_GOLD gold, 60% with the pity rules. */
export function gildOdds(p: Profile, item: Item, boosts: RollBoosts = {}): SimpleRollOdds {
  const cost: ResourceBundle = { scales: { eternal: GILD_SCALES }, gold: GILD_GOLD };
  if (boosts.venom) cost.venom = Math.floor(boosts.venom);
  if (boosts.blessed) cost.blessed = 1;
  const short = shortfall(p, cost);
  const f = itemForge(item);
  const odds = rollChance(GILD_BASE, f, boosts);
  const blocked = (f.gilding ?? 0) >= GILD_MAX ? 'maxGilding' : Object.keys(short).length ? (Object.keys(short).length === 1 && short.gold ? 'notEnoughGold' : 'cantAfford') : null;
  return { odds, cost, refundOnFail: refundOf(cost), shortfall: short, guaranteedWithin: guaranteedWithin(f, odds.chance), blocked };
}

/** Refine cost: Moltskin (1 = reroll both, 2 = keep one) + REFINE_GOLD × grade gold. */
export function refineCost(item: Item, moltskin: 1 | 2 = 1): ResourceBundle {
  return { moltskin, gold: Math.round(REFINE_GOLD * GRADE_GOLD[itemBandGrade(item)]) };
}

export const catalystCost = (): ResourceBundle => ({ sigils: 1, fangsteel: CATALYST_FANGSTEEL });

// ============================================================================
// +forge-sinks: late Sigil and Eye-fragment sinks (crafting.md §4.6)
// ============================================================================

export type ReshapeBlock = 'unknownItem' | 'notTierPiece' | 'pendingRefine' | 'badIndex' | 'cantAfford' | 'notEnoughGold';

/** Sigil Reshape preview: cost and whether it can run. Pure. */
export function sigilReshapeOdds(p: Profile, itemOrId: Item | string, opts: { keepIndex?: number } = {}): { cost: ResourceBundle; shortfall: ResourceBundle; blocked: ReshapeBlock | null } {
  const item = typeof itemOrId === 'string' ? p.inventory.find((i) => i.id === itemOrId) : itemOrId;
  const cost: ResourceBundle = { sigils: 1, gold: Math.round(SIGIL_RESHAPE_GOLD * GRADE_GOLD[item ? itemBandGrade(item) : 'ashen']) };
  const short = shortfall(p, cost);
  const k = opts.keepIndex;
  const blocked: ReshapeBlock | null = !item
    ? 'unknownItem'
    : !item.setId
      ? 'notTierPiece'
      : p.forge.pendingRefine
        ? 'pendingRefine'
        : k !== undefined && (k < 0 || k >= item.secondaries.length)
          ? 'badIndex'
          : Object.keys(short).length
            ? Object.keys(short).length === 1 && short.gold
              ? 'notEnoughGold'
              : 'cantAfford'
            : null;
  return { cost, shortfall: short, blocked };
}

export type AttuneBlock = 'maxAttunement' | 'cantAfford' | 'notEnoughGold';

/** Eye Attunement preview: the next Eye's Legendary chance now and after one attunement. Pure. */
export function eyeAttuneOdds(p: Profile): { cost: ResourceBundle; shortfall: ResourceBundle; chanceNow: number; chanceAfter: number; blocked: AttuneBlock | null } {
  const cost: ResourceBundle = { eyeFragments: EYE_ATTUNE_FRAGMENTS, gold: EYE_ATTUNE_GOLD };
  const short = shortfall(p, cost);
  const misses = p.forge.eyeMisses;
  const maxed = misses >= EYE_CHANCES.length - 1;
  const blocked: AttuneBlock | null = maxed ? 'maxAttunement' : Object.keys(short).length ? (Object.keys(short).length === 1 && short.gold ? 'notEnoughGold' : 'cantAfford') : null;
  return { cost, shortfall: short, chanceNow: eyeChance(misses), chanceAfter: eyeChance(Math.min(EYE_CHANCES.length - 1, misses + 1)), blocked };
}

// ============================================================================
// Cost tables and hints (UI "how odds work" / "what one more run buys")
// ============================================================================

/** Every temper target with its base % and cost (the "How odds work" table). */
export function temperTable(): { target: number; base: number; grade: ScaleGrade; fangsteel: number; scales: number; gold: number }[] {
  const out: { target: number; base: number; grade: ScaleGrade; fangsteel: number; scales: number; gold: number }[] = [];
  for (let t = 205; t <= 270; t += 5) {
    const g = gradeForIlvl(t);
    out.push({ target: t, base: temperBase(t), grade: g, ...TEMPER_COST[g] });
  }
  return out;
}

/** Rarity odds per Luck 0..LUCK_CAP (the craft odds table). */
export function rarityTable(): { luck: number; odds: RarityOdds; rarePlus: number }[] {
  const out: { luck: number; odds: RarityOdds; rarePlus: number }[] = [];
  for (let l = 0; l <= LUCK_CAP; l++) {
    const odds = baseRarityOdds(l);
    out.push({ luck: l, odds, rarePlus: odds.rare + odds.epic });
  }
  return out;
}

/** Masterwork table (step, base %, cost). */
export function masterworkTable(): { step: number; base: number; fangsteel: number; scales: number; gold: number }[] {
  return MW_COST.map((c, i) => ({ step: i + 1, base: MW_BASE[i]!, ...c }));
}

/** Expected value of an item roll in rarity terms: P(Rare or better), P(Epic+), for a spec. */
export function craftExpectation(odds: Pick<CraftOdds, 'rarity'>): { rarePlus: number; epicPlus: number } {
  const r = odds.rarity;
  return { rarePlus: r.rare + r.epic + r.legendary, epicPlus: r.epic + r.legendary };
}

/** Sum of rarity indexes weighted by odds (for sorting presets). */
export function expectedRarityIndex(odds: RarityOdds): number {
  return RARITIES.reduce((s, r) => s + (odds[r] / 100) * rarityIndex(r), 0);
}

// ---- small profile helpers (kept here so forge.odds does not import profile.ts) ----

const totalRatingOf = (p: Pick<Profile, 'rating'>): number => totalRating(p.rating);
export const standingTier = (p: Pick<Profile, 'rating'>) => tierFor(totalRatingOf(p)).tier;

/**
 * "What one more run buys" (crafting.md §6.1): the base Offering of a run at `level` and how
 * many temper steps its Scales buy for the main character's lowest item (expected attempts and
 * refunds included), plus whole crafts. A hint, not a promise: no Eye roll, world finds, daily
 * or bracket lines.
 */
export function nextRunHint(p: Profile, level: number, timed = true, chest: 0 | 1 | 2 | 3 = 1): { resources: ResourceBundle; grade: ScaleGrade; temperSteps: number; crafts: number } {
  const tier = standingTier(p);
  const est = estimateRunResources(level, timed, chest, tier);
  const grade = est.grade;
  const scales = est.base.scales?.[grade] ?? 0;
  const fangsteel = est.base.fangsteel ?? 0;
  const cap = tierDef(tier).maxIlvl;
  let lowest: Item | undefined;
  const ch = p.characters[p.mainClassId];
  for (const id of Object.values(ch?.gear ?? {})) {
    const it = p.inventory.find((i) => i.id === id);
    if (it && it.ilvl < cap && (!lowest || it.ilvl < lowest.ilvl)) lowest = it;
  }
  let temperSteps = 0;
  if (lowest) {
    const target = Math.min(cap, lowest.ilvl + 5);
    const g = gradeForIlvl(Math.max(target, 201));
    const c = TEMPER_COST[g];
    const tries = expectedAttempts(temperBase(target), { fails: 0, resonance: 0 }).expected;
    const net = 1 + (tries - 1) * (1 - REFUND_FRACTION);
    const usable = SCALE_ORDER.indexOf(grade) >= SCALE_ORDER.indexOf(g) ? scales : 0;
    temperSteps = Math.floor(Math.min(usable / (c.scales * net), fangsteel / (c.fangsteel * net)));
  }
  const crafts = Math.floor(Math.min(scales / CRAFT_SCALES, fangsteel / CRAFT_FANGSTEEL));
  return { resources: est.base, grade, temperSteps, crafts };
}

const SCALE_ORDER: readonly ScaleGrade[] = ['ashen', 'jade', 'gilded', 'eternal'];
